if (!customElements.get('size-guide-content')) {
  customElements.define('size-guide-content', class extends HTMLElement {
    constructor() {
      super();
      this.initialize = this.initialize.bind(this);
      this.handleUnitClick = this.handleUnitClick.bind(this);
      this.updateScrollHint = this.updateScrollHint.bind(this);
      this.measurements = [];
      this.headers = [];
      this.tableRegions = [];
    }

    connectedCallback() {
      this.addEventListener('click', this.handleUnitClick);

      if (this.initialized) {
        this.observeTables();
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initialize, { once: true });
      } else {
        queueMicrotask(this.initialize);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleUnitClick);
      document.removeEventListener('DOMContentLoaded', this.initialize);
      this.resizeObserver?.disconnect();
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;

      const body = this.querySelector('.size-guide__body');
      const toolbar = this.querySelector('.size-guide__toolbar');
      if (!body || !toolbar) return;

      this.initialized = true;
      this.scrollHint = this.querySelector('.size-guide__scroll-hint');
      this.unitButtons = Array.from(this.querySelectorAll('[data-size-guide-unit]'));
      const title = this.querySelector('.size-guide__title')?.textContent.trim() || this.dataset.sizeGuideTitle || '';

      body.querySelectorAll('table').forEach((table) => {
        const headerRow = table.tHead?.rows[table.tHead.rows.length - 1] || table.rows[0];
        if (!headerRow) return;

        Array.from(headerRow.cells).forEach((header, columnIndex) => {
          if (columnIndex === 0 || header.colSpan !== 1 || !/\(\s*cm\s*\)/i.test(header.textContent)) return;

          const columnMeasurements = [];

          Array.from(table.rows).forEach((row) => {
            if (row === headerRow || row.closest('thead')) return;
            const cell = row.cells[columnIndex];
            if (!cell || cell.colSpan !== 1) return;
            const inches = this.convertMeasurement(cell.textContent);
            if (inches === null) return;
            columnMeasurements.push({ element: cell, centimeters: cell.innerHTML, inches });
          });

          if (columnMeasurements.length === 0) return;

          const inchHeader = header.cloneNode(true);
          const textNodes = document.createTreeWalker(inchHeader, NodeFilter.SHOW_TEXT);
          let textNode;
          while ((textNode = textNodes.nextNode())) {
            textNode.textContent = textNode.textContent.replace(/\(\s*cm\s*\)/gi, '(in)');
          }

          this.headers.push({ element: header, centimeters: header.innerHTML, inches: inchHeader.innerHTML });
          this.measurements.push(...columnMeasurements);
        });

        const region = document.createElement('div');
        region.className = 'size-guide__table-scroll';
        region.tabIndex = 0;
        region.setAttribute('role', 'region');
        region.setAttribute('data-scroll-lock-scrollable', '');
        region.setAttribute('aria-label', title || this.scrollHint?.textContent.trim() || toolbar.textContent.trim());
        table.before(region);
        region.append(table);
        this.tableRegions.push(region);
      });

      toolbar.hidden = this.dataset.sizeGuideUnitsEnabled === 'false' || this.measurements.length === 0;
      this.observeTables();
    }

    convertMeasurement(value) {
      const text = value.replace(/\u00a0/g, ' ').trim();
      const match = text.match(/^(\d+(?:[.,]\d+)?)(\s*(?:[-–—−]|to|a|do)\s*(\d+(?:[.,]\d+)?))?(\s*cm)?$/i);
      if (!match) return null;

      const decimalMark = text.includes(',') ? ',' : '.';
      const toInches = (number) => {
        const centimeters = Number(number.replace(',', '.'));
        if (!Number.isFinite(centimeters)) return null;
        return String(Number((centimeters / 2.54).toFixed(2))).replace('.', decimalMark);
      };

      const first = toInches(match[1]);
      const last = match[3] === undefined ? null : toInches(match[3]);
      if (first === null || (match[3] !== undefined && last === null)) return null;

      const range = match[3] === undefined ? '' : match[2].slice(0, -match[3].length) + last;
      return first + range + (match[4] ? match[4].replace(/cm/i, 'in') : '');
    }

    handleUnitClick(event) {
      if (this.dataset.sizeGuideUnitsEnabled === 'false') return;
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-size-guide-unit]');
      if (!button || button.closest('size-guide-content') !== this || !this.initialized) return;
      const unit = button.dataset.sizeGuideUnit;
      if (unit !== 'cm' && unit !== 'in') return;

      this.measurements.forEach(({ element, centimeters, inches }) => {
        if (unit === 'cm') element.innerHTML = centimeters;
        else element.textContent = inches;
      });

      this.headers.forEach(({ element, centimeters, inches }) => {
        element.innerHTML = unit === 'cm' ? centimeters : inches;
      });

      this.unitButtons.forEach((unitButton) => {
        unitButton.setAttribute('aria-pressed', String(unitButton.dataset.sizeGuideUnit === unit));
      });

      this.updateScrollHint();
    }

    observeTables() {
      if ('ResizeObserver' in window) {
        this.resizeObserver ??= new ResizeObserver(this.updateScrollHint);
        this.tableRegions.forEach((region) => this.resizeObserver.observe(region));
      }
      this.updateScrollHint();
    }

    updateScrollHint() {
      if (!this.scrollHint) return;
      this.scrollHint.hidden = !this.tableRegions.some((region) => region.clientWidth > 0 && region.scrollWidth > region.clientWidth + 1);
    }
  });
}
